import React, { useState, useEffect } from "react";
import {
  Search,
  ChevronDown,
  MoreVertical,
  Edit,
  Eye,
  Trash2,
  Tag,
  Percent,
  CreditCard,
  TrendingUp,
  Loader2,
} from "lucide-react";
import {
  getDiscountStats,
  getDiscountCodes,
  getTopCodesByRevenue,
  getMostUsedCodesByRedemptions,
  updateDiscountCode,
  deleteDiscountCode,
} from "../../../services/discountService";
import { toast } from "react-toastify";
import { useDebounce } from "../../../hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import Pagination from "../../Common/Pagination";

const ITEMS_PER_PAGE = 10;

const DiscountManagement = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Codes");
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [topByRevenue, setTopByRevenue] = useState([]);
  const [mostUsedByRedemptions, setMostUsedByRedemptions] = useState([]);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const getStatusDisplay = (item) => {
    if (item.isDeleted || item.status === 'deleted') {
      return {
        label: 'Deleted',
        dotColor: 'bg-red-500',
        badgeClass: 'bg-red-100 text-red-700'
      };
    }
    if (item.status === 'disabled') {
      return {
        label: 'Disabled',
        dotColor: 'bg-red-500',
        badgeClass: 'bg-red-100 text-red-700'
      };
    }
    if (item.status === 'fully_redeemed') {
      return {
        label: 'Fully Redeemed',
        dotColor: 'bg-amber-500',
        badgeClass: 'bg-amber-100 text-amber-800'
      };
    }
    if (item.status === 'expired') {
      return {
        label: 'Expired',
        dotColor: 'bg-gray-400',
        badgeClass: 'bg-gray-100 text-gray-600'
      };
    }

    const now = new Date();
    const start = item.startAt || item.startDate;
    const end = item.endAt || item.expiryDate;

    if (end && new Date(end) < now) {
      return {
        label: 'Expired',
        dotColor: 'bg-gray-400',
        badgeClass: 'bg-gray-100 text-gray-600'
      };
    }

    if (start && new Date(start) > now) {
      return {
        label: 'Scheduled',
        dotColor: 'bg-blue-500',
        badgeClass: 'bg-blue-100 text-blue-700'
      };
    }

    return {
      label: 'Active',
      dotColor: 'bg-[#0F973D]',
      badgeClass: 'bg-green-100 text-green-800'
    };
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsData, codesData, revenueData, redemptionsData] = await Promise.all([
          getDiscountStats(),
          getDiscountCodes({
            search: debouncedSearch,
            page: currentPage,
            limit: ITEMS_PER_PAGE,
            status: filterStatus === "All Codes" ? null : filterStatus,
          }),
          getTopCodesByRevenue(),
          getMostUsedCodesByRedemptions(),
        ]);
        setStats(statsData);
        setCodes(codesData.codes || []);
        setPagination(codesData.pagination || {});
        setTopByRevenue(revenueData || []);
        setMostUsedByRedemptions(redemptionsData || []);
      } catch (err) {
        console.error("Failed to load discount data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [debouncedSearch, currentPage, filterStatus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdownId && !e.target.closest(`[data-dropdown="${openDropdownId}"]`)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  const statsCards = stats
    ? [
      {
        title: "Active codes",
        value: stats.activeCodes?.toString() || "0",
        icon: Tag,
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600",
      },
      {
        title: "Total Redemptions",
        value: stats.totalRedemptions?.toLocaleString() || "0",
        icon: Percent,
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600",
      },
      {
        title: "Total Discount Granted",
        value: `$${(stats.totalDiscountGranted || 0).toLocaleString()}`,
        icon: CreditCard,
        iconBg: "bg-green-100",
        iconColor: "text-green-600",
      },
      {
        title: "Total Revenue Influenced",
        value: `$${(stats.totalRevenueInfluenced || 0).toLocaleString()}`,
        icon: TrendingUp,
        iconBg: "bg-orange-100",
        iconColor: "text-orange-600",
      },
    ]
    : [];

  const toggleDropdown = (id) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleStatusConfirm = async () => {
    if (!selectedCode) return;
    try {
      const newStatus = selectedCode.status === 'disabled' ? 'active' : 'disabled';
      await updateDiscountCode(selectedCode._id || selectedCode.id, { status: newStatus });
      toast.success(`Discount code ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);

      // Refresh list
      const codesData = await getDiscountCodes({
        search: debouncedSearch,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        status: filterStatus === "All Codes" ? null : filterStatus,
      });
      setCodes(codesData.codes || []);
      setPagination(codesData.pagination || {});

      // Update stats
      const [statsData, revenueData, redemptionsData] = await Promise.all([
        getDiscountStats(),
        getTopCodesByRevenue(),
        getMostUsedCodesByRedemptions(),
      ]);
      setStats(statsData);
      setTopByRevenue(revenueData || []);
      setMostUsedByRedemptions(redemptionsData || []);
    } catch (err) {
      console.error(`Failed to ${selectedCode.status === 'disabled' ? 'enable' : 'disable'} discount code:`, err);
      toast.error(err.message || "Failed to update discount code status");
    } finally {
      setIsStatusModalOpen(false);
      setSelectedCode(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCode) return;
    try {
      await deleteDiscountCode(selectedCode._id || selectedCode.id);
      toast.success("Discount code deleted successfully");

      // Refresh list
      const codesData = await getDiscountCodes({
        search: debouncedSearch,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        status: filterStatus === "All Codes" ? null : filterStatus,
      });
      setCodes(codesData.codes || []);
      setPagination(codesData.pagination || {});

      // Update stats
      const [statsData, revenueData, redemptionsData] = await Promise.all([
        getDiscountStats(),
        getTopCodesByRevenue(),
        getMostUsedCodesByRedemptions(),
      ]);
      setStats(statsData);
      setTopByRevenue(revenueData || []);
      setMostUsedByRedemptions(redemptionsData || []);
    } catch (err) {
      console.error("Failed to delete discount code:", err);
      toast.error(err.message || "Failed to delete discount code");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedCode(null);
    }
  };

  return (
    <div className="min-h-screen p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6">
      {/* Stats Cards - matching Figma layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded mb-2 w-24" />
                  <div className="h-8 bg-gray-200 rounded w-16" />
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full" />
              </div>
            </div>
          ))
        ) : (
          statsCards.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.iconBg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Discount Code List Section */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">Discount Code List</h2>
            <button
              onClick={() => navigate("/admin-dashboard/discount-management/create")}
              className="inline-flex items-center justify-center px-4 py-2.5 bg-[#075038] text-white text-sm font-medium rounded-2xl hover:bg-[#075038]/90 transition-colors"
            >
              Create Code
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or code"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-[#075038] focus:border-transparent"
              />
            </div>
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="appearance-none w-full sm:w-48 pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-[#075038] focus:border-transparent"
              >
                <option>All Codes</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="disabled">Disabled</option>
                <option value="fully_redeemed">Fully Redeemed</option>
                <option value="deleted">Deleted</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#075038]" />
            </div>
          ) : codes.length === 0 ? (
            <div className="p-12 text-center">
              <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No discount codes found</p>
              <p className="text-gray-400 text-sm mt-1">Create your first discount code to get started</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Eligibility
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Plans
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Total Usage
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Expiry
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {codes.map((item) => (
                  <tr key={item._id || item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-md bg-[#F4F8F7] text-black-700">
                        {item.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-3 py-1 text-xs font-medium rounded-2xl bg-white border border-gray-200 text-gray-700">
                        {item.eligibility}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.discount}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {Array.isArray(item.selectedPlans)
                        ? item.selectedPlans.map((p) => (typeof p === "object" ? (p.name || p.id) : p)).join(", ")
                        : item.selectedPlans || "—"}
                    </td>
                    <td className="px-6 py-4">
                      {item.totalUsage ? (
                        item.totalUsage.limit == null || item.globalCap === null ? (
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-900 font-medium">
                                {item.totalUsage.used} / Unlimited
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-900 font-medium">
                                {item.totalUsage.used}/{item.totalUsage.limit}
                              </span>
                              <span className="text-gray-500">
                                {item.totalUsage.limit - item.totalUsage.used} left
                              </span>
                            </div>
                            <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${(item.totalUsage.used / item.totalUsage.limit) * 100 > 90
                                  ? "bg-[#EB0000]"
                                  : (item.totalUsage.used / item.totalUsage.limit) * 100 >= 70
                                    ? "bg-[#F5C956]"
                                    : "bg-[#0F973D]"
                                  }`}
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (item.totalUsage.used / item.totalUsage.limit) * 100
                                  )}%`,
                                }}
                              />
                            </div>
                          </div>
                        )
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {item.endAt ? new Date(item.endAt).toLocaleDateString() : "No Expiry"}
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const status = getStatusDisplay(item);
                        return (
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${status.badgeClass}`}>
                            {status.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative" data-dropdown={item._id || item.id}>
                        <button
                          onClick={() => toggleDropdown(item._id || item.id)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>
                        {openDropdownId === (item._id || item.id) && (
                          <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                            <button
                              onClick={() => {
                                setOpenDropdownId(null);
                                navigate(`/admin-dashboard/discount-management/view/${item._id}`);
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              View Code Details
                            </button>
                            {!item.isDeleted && (
                              <button
                                onClick={() => {
                                  setOpenDropdownId(null);
                                  navigate("/admin-dashboard/discount-management/create", { state: { editData: item } });
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                Edit Code
                              </button>
                            )}
                            {!item.isDeleted && item.status !== 'deleted' && item.status !== 'disabled' && (
                              <button
                                onClick={() => {
                                  setOpenDropdownId(null);
                                  setSelectedCode(item);
                                  setIsStatusModalOpen(true);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                Disable Code
                              </button>
                            )}
                            {!item.isDeleted && item.status !== 'deleted' && item.status === 'disabled' && (
                              <button
                                onClick={() => {
                                  setOpenDropdownId(null);
                                  setSelectedCode(item);
                                  setIsStatusModalOpen(true);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                              >
                                Enable Code
                              </button>
                            )}
                            {/* <button
                              onClick={() => {
                                setOpenDropdownId(null);
                                setSelectedCode(item);
                                setIsDeleteModalOpen(true);
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100 mt-1"
                            >
                              Delete Code
                            </button> */}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && codes.length > 0 && (
          <div className="mt-6 px-6 pb-6">
            <Pagination
              currentPage={pagination.currentPage || 1}
              totalPages={pagination.totalPages || 1}
              onPageChange={setCurrentPage}
              hasNextPage={pagination.hasNextPage || false}
              hasPrevPage={pagination.hasPrevPage || false}
              totalItems={pagination.totalUsers || pagination.total || 0}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </div>

      {/* Bottom cards - Top Performing By Revenue & Most Used By Redemptions */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Top Performing Codes By Revenue */}
        <div className="bg-white rounded-2xl border border-[#E0E4E2] overflow-hidden">
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Top Performing Codes By Revenue</h3>
            <div className="h-px bg-[#EAECF0] mb-4" />
            <div className="space-y-4">
              {topByRevenue.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">No data available</p>
              ) : (
                topByRevenue.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-[#EAECF0] last:border-0">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const status = getStatusDisplay(item);
                        return (
                          <>
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${status.dotColor}`} />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.name}</p>
                              <p className="text-xs text-[#7E726B]">{item.code}</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-3">
                      {(() => {
                        const status = getStatusDisplay(item);
                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-2xl ${status.badgeClass}`}>
                            {status.label}
                          </span>
                        );
                      })()}
                      <span className="text-sm font-medium text-gray-900">
                        ${(item.value || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Most Used Codes By Redemptions */}
        <div className="bg-white rounded-2xl border border-[#E0E4E2] overflow-hidden">
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Most Used Codes By Redemptions</h3>
            <div className="h-px bg-[#EAECF0] mb-4" />
            <div className="space-y-4">
              {mostUsedByRedemptions.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">No data available</p>
              ) : (
                mostUsedByRedemptions.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-[#EAECF0] last:border-0">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const status = getStatusDisplay(item);
                        return (
                          <>
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${status.dotColor}`} />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.name}</p>
                              <p className="text-xs text-[#7E726B]">{item.code}</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-3">
                      {(() => {
                        const status = getStatusDisplay(item);
                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-2xl ${status.badgeClass}`}>
                            {status.label}
                          </span>
                        );
                      })()}
                      <span className="text-sm font-medium text-gray-900">
                        {(item.value || 0).toLocaleString()} uses
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Confirmation Modal */}
      {isStatusModalOpen && selectedCode && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] w-full max-w-[500px] p-8 sm:p-10 text-center shadow-xl">
            <h3 className="text-[28px] font-bold text-[#111827] mb-3 font-space-grotesk">
              {selectedCode.status === 'disabled' ? 'Enable Discount Code?' : 'Disable Discount Code?'}
            </h3>
            <div className="flex justify-center mb-6">
              <div className={`p-4 rounded-full ${selectedCode.status === 'disabled' ? 'bg-green-100' : 'bg-red-100'}`}>
                {selectedCode.status === 'disabled' ? (
                  <Tag className="w-8 h-8 text-green-600" />
                ) : (
                  <Tag className="w-8 h-8 text-red-600" />
                )}
              </div>
            </div>
            <p className="text-[15px] text-[#4B5563] mb-8 font-medium">
              {selectedCode.status === 'disabled'
                ? 'This code will become usable again for new checkouts'
                : 'This code will no longer be usable for new checkouts'}
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setIsStatusModalOpen(false);
                  setSelectedCode(null);
                }}
                className="flex-1 px-4 py-3.5 text-[15px] font-semibold text-[#111827] bg-white border border-[#E0E4E2] rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusConfirm}
                className={`flex-1 px-4 py-3.5 text-[15px] font-semibold text-white rounded-xl transition-colors ${selectedCode.status === 'disabled'
                  ? 'bg-[#206954] hover:bg-[#1b5846]'
                  : 'bg-red-600 hover:bg-red-700'
                  }`}
              >
                {selectedCode.status === 'disabled' ? 'Enable Code' : 'Disable Code'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedCode && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] w-full max-w-[500px] p-8 sm:p-10 text-center shadow-xl">
            <h3 className="text-[28px] font-bold text-[#111827] mb-3 font-space-grotesk">
              Delete Discount Code?
            </h3>
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-red-100">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <p className="text-[15px] text-[#4B5563] mb-8 font-medium px-4">
              Are you sure you want to delete <span className="font-bold text-gray-900">{selectedCode.code}</span>? This action cannot be undone and the code will be permanently removed.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedCode(null);
                }}
                className="flex-1 px-4 py-3.5 text-[15px] font-semibold text-[#111827] bg-white border border-[#E0E4E2] rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-3.5 text-[15px] font-semibold text-white bg-[#cc0000] hover:bg-[#aa0000] rounded-xl transition-colors"
              >
                Delete Code
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountManagement;
